import { VariantProps } from 'class-variance-authority'
import React from 'react'
import { match } from 'ts-pattern'

import { LoadingSpinner } from '@frame/ui-components/loading-spinner'
import { Button, buttonVariants } from '@frame/ui-primitives/button'
import { Card, CardProps } from '@frame/ui-primitives/card'
import { H4 } from '@frame/ui-primitives/h4'
import { P } from '@frame/ui-primitives/p'
import { Span } from '@frame/ui-primitives/span'

import { TrueworkJsStrategy } from '@frame/lib/api/tasks/types'
import { useStore } from '@frame/lib/stores/useStore'
import { getVerifierDisplayName } from '@frame/lib/utils/session'
import { DataAttribute } from '@frame/types'

type Props = {
  intent?: CardProps['intent']
  strategy: TrueworkJsStrategy
  loading?: boolean
  disabled?: boolean
  onClick?: () => void
}

function getVerificationMethodCardConfig(strategy: TrueworkJsStrategy) {
  return match(strategy)
    .with(TrueworkJsStrategy.CREDENTIALS, () => ({
      title: "Log into employer's payroll account",
      subTitle: 'Fastest and most accurate',
      button: {
        text: 'Log into payroll account',
        variant: 'primary' as VariantProps<typeof buttonVariants>['intent'],
      },
      dataCy: DataAttribute.VERIFY_VIA_CREDENTIALS_BUTTON,
    }))
    .with(TrueworkJsStrategy.DOCUMENTS, () => ({
      title: 'Upload documents',
      subTitle: 'Alternative',
      button: {
        text: 'Upload documents',
        variant: 'secondary' as VariantProps<typeof buttonVariants>['intent'],
      },
      dataCy: DataAttribute.VERIFY_VIA_DOCUMENTS_BUTTON,
    }))
    .with(TrueworkJsStrategy.BANK, () => ({
      title: 'Bank account',
      subTitle: 'Easiest and most efficient',
      button: {
        text: 'Connect bank account',
        variant: 'primary' as VariantProps<typeof buttonVariants>['intent'],
      },
      dataCy: DataAttribute.VERIFY_VIA_BANK_BUTTON,
    }))
    .with(TrueworkJsStrategy.EMPLOYMENT_STRATEGIES, () => ({
      title: 'Employment history',
      subTitle: 'Alternative',
      button: {
        text: 'Provide employment details',
        variant: 'secondary' as VariantProps<typeof buttonVariants>['intent'],
      },
      dataCy: DataAttribute.VERIFY_VIA_EMPLOYMENT_BUTTON,
    }))
    .with(TrueworkJsStrategy.INSTANT, () => ({
      title: null,
      subTitle: null,
      button: {
        text: null,
        variant: null,
      },
      dataCy: null,
    }))
    .with(TrueworkJsStrategy.SMART_OUTREACH, () => ({
      title: null,
      subTitle: null,
      button: {
        text: null,
        variant: null,
      },
      dataCy: null,
    }))
    .with(TrueworkJsStrategy.IRS, () => ({
      title: null,
      subTitle: null,
      button: {
        text: null,
        variant: null,
      },
      dataCy: null,
    }))
    .exhaustive()
}

function getVerificationMethodCardDescription(strategy: TrueworkJsStrategy, verifierName?: string) {
  return match(strategy)
    .with(
      TrueworkJsStrategy.CREDENTIALS,
      () => `
          Your login credentials will never be ${verifierName && `made accessible to ${verifierName} or`} stored by
          Truework.
        `
    )
    .with(TrueworkJsStrategy.DOCUMENTS, () => 'You will need your most recent paystubs or W2.')
    .with(TrueworkJsStrategy.BANK, () => 'Connect your bank account as a quick and accurate way to verify your income.')
    .with(
      TrueworkJsStrategy.EMPLOYMENT_STRATEGIES,
      () => 'Provide your employment details and choose a verification method.'
    )
    .with(TrueworkJsStrategy.INSTANT, () => null)
    .with(TrueworkJsStrategy.SMART_OUTREACH, () => null)
    .with(TrueworkJsStrategy.IRS, () => null)
    .exhaustive()
}

export function VerificationMethodCard({
  intent = 'primary',
  strategy,
  loading = false,
  disabled = false,
  onClick,
}: Props) {
  const { verifierName, verifierType, tenantPropertyName } = useStore((state) => ({
    verifierName: state.session?.originating_party_display_name,
    verifierType: state.session?.verifier_type,
    tenantPropertyName: state.session?.configuration?.cobranding?.tenant_property?.property_name,
  }))
  const { title, subTitle, button, dataCy } = getVerificationMethodCardConfig(strategy)
  const displayName = getVerifierDisplayName(verifierType, verifierName, tenantPropertyName)
  const description = getVerificationMethodCardDescription(strategy, displayName)

  return (
    <Card intent={intent} className="p-5">
      <Span className="mb-2 text-xs font-semibold text-cobranding">{subTitle}</Span>
      <H4 className="mb-2">{title}</H4>
      <P variant="small" className="mb-4">
        {description}
      </P>
      <Button
        data-cy={dataCy}
        intent={intent as VariantProps<typeof buttonVariants>['intent']}
        onClick={onClick}
        disabled={disabled}
        Icon={loading ? LoadingSpinner : undefined}
        applyCobranding={intent === 'primary'}
      >
        {button.text}
      </Button>
    </Card>
  )
}
